import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, Grid, TextField, Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { PlanData } from "types/models/planMaster/planMasterApp";
import { useDispatch } from "react-redux";
import { createPlan } from "redux/actions/planMaster";
import planValidationSchema from "../planValidationSchema";

interface PlanProps {
  Plan?: PlanData;
}
  
const PlanCreate: React.FC<PlanProps> = ({ Plan }) => {
  const navigate = useNavigate();
  const [fileError, setFileError] = useState(false);
  const dispatch = useDispatch();
  const initialValues = useMemo(
    () => ({
      planCategory: Plan?.planCategory || "Base",
      planClassification: Plan?.planClassification || "Special Plan",
      planDuration: Plan?.planDuration || 1,
      validity: Plan?.validity || 30,
      planName: Plan?.planName || "",
      planCode: Plan?.planCode || "",
      speedType: Plan?.speedType || "Mbps",
      rentExclTax: Plan?.rentExclTax ?? 0,
      rentInclTax: Plan?.rentInclTax ?? 0,
      rentlTax: Plan?.rentlTax != null && Plan.rentlTax > 0 ? Plan.rentlTax : 1,
  
      primaryQuota: Plan?.primaryQuota ?? 0,
      secondaryQuota: Plan?.secondaryQuota != null ? Plan.secondaryQuota : 1,
      planType: Plan?.planType || "WiFi",
      popPlan: Plan?.popPlan || "N",
      popBestSeller: Plan?.popBestSeller || "Y",
      status: Plan?.status || "Active",
      popLable: Plan?.popLable || "",
      popPrice: Plan?.popPrice || 0,
      popDuration: Plan?.popDuration || "",
      popOffer: Plan?.popOffer || "",
    }),
    [Plan]
  );
  
  const handleSubmit = useCallback(
    async (values: any) => {
      const dataPayload = {
        ...values,
        popLable: values.popPlan === "Y" ? values.popLable : undefined,
        popPrice: values.popPlan === "Y" ? values.popPrice : undefined,
        popDuration: values.popPlan === "Y" ? values.popDuration : undefined,
        popOffer: values.popPlan === "Y" ? values.popOffer : undefined,
      };
      
      try {
        const response = (await dispatch(createPlan(dataPayload))) as any;
        if (response.status === 200) {
          navigate("/planMaster/ViewPlan/plan");
        }
      } catch (error) {
        console.error("Error creating plan:", error);
      }
    },
    [dispatch, navigate]
  );


  return (
    <Box sx={{ height: "100%", width: "100%", m: 8 }}>
      <Grid sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%", margin: 2 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={planValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Category"
                    name="planCategory"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.planCategory && !!errors.planCategory}
                    helperText={<ErrorMessage name="planCategory" />}
                  >
                    <option value="Base">Base</option>
                    <option value="Limited">Limited</option>
                    <option value="Unlimited">Unlimited</option>
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Classification"
                    name="planClassification"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.planClassification && !!errors.planClassification}
                    helperText={<ErrorMessage name="planClassification" />}
                  >
                    <option value="Special Plan">Special Plan</option>
                    <option value="Commercial">Commercial</option>
                  </Field>
                </Grid>
              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Name"
                    name="planName"
                    variant="outlined"
                    error={touched.planName && !!errors.planName}
                    helperText={<ErrorMessage name="planName" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Code"
                    name="planCode"
                    variant="outlined"
                    error={touched.planCode && !!errors.planCode}
                    helperText={<ErrorMessage name="planCode" />}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Speed Type"
                    name="speedType"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.speedType && !!errors.speedType}
                    helperText={<ErrorMessage name="speedType" />}
                  >
                    <option value="Mbps">Mbps</option>
                    <option value="Kbps">Kbps</option>
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Type"
                    name="planType"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.planType && !!errors.planType}
                    helperText={<ErrorMessage name="planType" />}
                  >
                    <option value="1">WiFi</option>
                    <option value="2">WiFi + OTT</option>
                  </Field>
                </Grid>
              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Plan Duration"
                    name="planDuration"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.planDuration && !!errors.planDuration}
                    helperText={<ErrorMessage name="planDuration" />}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      const durationMapping = {
                        "1": "Monthly",
                        "3": "Quarterly",
                        "6": "Half Yearly",
                        "12": "Yearly",
                      };

                      setFieldValue("planDuration", selectedValue);
                      setFieldValue("planDuration", durationMapping[selectedValue]);
                    }}
                  >
                    <option value="1">Monthly</option>
                    <option value="3">Quarterly</option>
                    <option value="6">Half Yearly</option>
                    <option value="12">Yearly</option>
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Validity"
                    name="validity"
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.validity && !!errors.validity}
                    helperText={<ErrorMessage name="validity" />}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Primary Quota (In GB)"
                    name="primaryQuota"
                    variant="outlined"
                    error={touched.primaryQuota && !!errors.primaryQuota}
                    helperText={<ErrorMessage name="primaryQuota" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Secondary Quota (In GB)"
                    name="secondaryQuota"
                    variant="outlined"
                    error={touched.secondaryQuota && !!errors.secondaryQuota}
                    helperText={<ErrorMessage name="secondaryQuota" />}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Base Price"
                    name="rentExclTax"
                    variant="outlined"
                    error={touched.rentExclTax && !!errors.rentExclTax}
                    helperText={<ErrorMessage name="rentExclTax" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Grid container >
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Tax"
                        name="rentlTax"
                        variant="outlined"
                        error={touched.rentlTax && !!errors.rentlTax}
                        helperText={<ErrorMessage name="rentlTax" />}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Price Inc. Tax"
                        name="rentInclTax"
                        variant="outlined"
                        error={touched.rentInclTax && !!errors.rentInclTax}
                        helperText={<ErrorMessage name="rentInclTax" />}
                      />
                    </Grid>
                  </Grid>

                </Grid>
              </Grid>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Propular Plan"
                    name="popPlan"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.popPlan && !!errors.popPlan}
                    helperText={<ErrorMessage name="popPlan" />}
                  >
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Propular Best Seller"
                    name="popBestSeller"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.popBestSeller && !!errors.popBestSeller}
                    helperText={<ErrorMessage name="popBestSeller" />}
                  >
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </Field>

                </Grid>
              </Grid>
              {values.popPlan === "Y" && (
                <>
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Label"
                        name="popLable"
                        variant="outlined"
                        error={touched.popLable && !!errors.popLable}
                        helperText={<ErrorMessage name="popLable" />}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Price"
                        name="popPrice"
                        type="number"
                        variant="outlined"
                        error={touched.popPrice && !!errors.popPrice}
                        helperText={<ErrorMessage name="popPrice" />}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Duration"
                        name="popDuration"
                        variant="outlined"
                        error={touched.popDuration && !!errors.popDuration}
                        helperText={<ErrorMessage name="popDuration" />}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Pop Offer"
                        name="popOffer"
                        variant="outlined"
                        error={touched.popOffer && !!errors.popOffer}
                        helperText={<ErrorMessage name="popOffer" />}
                      />
                    </Grid>
                  </Grid>
                </>

              )}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Status"
                    name="status"
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                    error={touched.status && !!errors.status}
                    helperText={<ErrorMessage name="status" />}
                  >
                    <option value="Active">Active</option>
                    <option value="InActive">Inactive</option>
                  </Field>
                </Grid>
              </Grid>

              <Box
                sx={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  display: "flex",
                  justifyContent: "right",
                  gap: 2,
                  paddingBottom: 2,
                  paddingTop: 2,
                  zIndex: 1000,
                  backgroundColor: "white",
                  boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  type="button"
                  onClick={() => navigate("/planMaster/ViewPlan/plan")}
                  sx={{ textTransform: "none", cursor: "pointer" }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ textTransform: "none", cursor: "pointer", right: 4 }}
                >
                  {isSubmitting ? "Submitting..." : "Add Plan"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Grid>

      <Snackbar
        open={fileError}
        autoHideDuration={6000}
        onClose={() => setFileError(false)}
      >
        <Alert onClose={() => setFileError(false)} severity="error" sx={{ width: "100%" }}>
          {fileError ? "You can only upload one image." : "Plan added successfully!"}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanCreate;
